import React, { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Search, Download, Upload, Send } from "lucide-react";
import {
  GridRowModes,
  DataGridPremium,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
  useGridApiContext,
  GridToolbarQuickFilter,
  GridToolbarFilterButton,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid-premium";
import { randomId } from "@mui/x-data-grid-generator";
import { read, utils, writeFile } from "xlsx-js-style";
import dayjs from "dayjs";
import { ModuleTable } from "./moduletable";
import Swal from "sweetalert2";
import _ from "lodash";
import axios from "../axios";
import { useNavigate } from "react-router-dom";
import Draggable from "react-draggable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import AdrawingtableDrawer from "./adrawingtableDrawer";
import { DrawingDiscPDList, DPsubPDList, DPsubPD2List } from "../../utils/publicVar";

export const FormTable = ({
  tabeldata,
  moduleData,
  areaList,
  vendorList,
  timeData,
  projectCode,
}) => {
  const [openSheet, setOpenSheet] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rows, setRows] = React.useState(
    tabeldata.map((v, index) => ({
      ...v,
      id: index + 1,
    }))
  );

  useEffect(() => {
    setRows(
      tabeldata.map((v, index) => ({
        ...v,
        id: index + 1,
      }))
    );
  }, [tabeldata]);

  const [rowModesModel, setRowModesModel] = React.useState({});
  let navigate = useNavigate();

  ///////////////////

  const downloadExcelModule = () => {
    const worksheet = utils.json_to_sheet(
      moduleData.map((v) => ({
        "레코드 번호": v.record_no,
        모듈넘버: v.MDModuleNumber,
        "모듈 위치": v.ModuleAreaSPD,
        "현장 입고 계획일": v.MDSitePlanDO,
      }))
      // .map((v)=>{ delete v.bItemID; delete v.inEdit; delete v.Description; return v; })
    );
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Sheet1");
    //let buffer = XLSX.write(workbook, { bookType: "xlsx", type: "buffer" });
    //XLSX.write(workbook, { bookType: "xlsx", type: "binary" });
    writeFile(workbook, "모듈정보.xlsx");
  };

  const handleOpenSheet = (row) => () => {
    const index = rows.findIndex((r) => r.id === row.id);
    setSelectedRowIndex(index);
    setEditRow({ ...row });
    setOpenSheet(true);
  };

  const handlePrevRow = () => {
    if (selectedRowIndex === null || selectedRowIndex === 0) return;
    const newIndex = selectedRowIndex - 1;
    setSelectedRowIndex(newIndex);
    setEditRow({ ...rows[newIndex] });
  };

  const handleSheetSave = () => {
    try {
      const requiredFields = [
        { field: "DrawingName", message: "도면명은 필수값입니다." },
        { field: "DPFloor", message: "층은 필수값입니다." },
        { field: "DrawingNo", message: "도면 번호는 필수값입니다." },
        { field: "DPsubPD2", message: "구분은 필수값입니다." },
        { field: "DPsubPD", message: "상세공종은 필수값입니다." },
        { field: "DrawingDiscPD", message: "공종은 필수값입니다." },
        { field: "DPAreaBPK", message: "Area는 필수값입니다." },
        { field: "DPRevNo", message: "도면 리비전 번호는 필수값입니다." },
      ];

      const date = new Date(editRow.WStart);
      const date2 = new Date(editRow.MDSitePlanDO);

      if (isNaN(date.getTime()) && isNaN(date2.getTime())) {
        throw new Error(
          `레코드 저장 중 에러 발생: 기준 날짜나 모듈 입고일 중 하나는 필수적으로 입력되어있어야합니다.`
        );
      }

      // Check each required field
      for (const { field, message } of requiredFields) {
        // Check if the value is not present
        if (!editRow[field]) {
          throw new Error(`레코드 저장 중 에러 발생: ${message}`);
        }

        // Check if the value is not a string or if the string value is empty
        if (typeof editRow[field] !== "string" || editRow[field].trim() === "") {
          throw new Error(`레코드 저장 중 에러 발생: ${message}`);
        }
      }

      const updatedRow = {
        ...editRow,
        isNew: false,
        WStart: editRow.WStart === null ? editRow.MDSitePlanDO : editRow.WStart,
        ReceiptPlanDate: editRow.ReceiptPlanDate,
        CompletionPlanDate: editRow.CompletionPlanDate,
        // IssuePlanDate: editRow.IssuePlanDate,
        SubmissionPlanDate: editRow.SubmissionPlanDate,
        ApprovalPlanDate: editRow.ApprovalPlanDate,
      };

      setRows((prev) => prev.map((row) => (row.id === editRow.id ? updatedRow : row)));
      setOpenSheet(false);
      setEditRow(null);
    } catch (error) {
      handleProcessRowUpdateError(error);
    }
  };

  const handleNextRow = () => {
    if (selectedRowIndex === null || selectedRowIndex === rows.length - 1) return;
    const newIndex = selectedRowIndex + 1;
    setSelectedRowIndex(newIndex);
    setEditRow({ ...rows[newIndex] });
  };

  const handleSelectionChange = (selection) => {
    setSelectionModel(selection);
  };

  const handleSelectRowDelete = () => {
    setRows(rows.filter((row) => !selectionModel.includes(row.id)));
    setSelectionModel([]);
  };

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  function EditToolbar(props) {
    const { setRows, setRowModesModel } = props;

    const gridButtonSx = {
      fontSize: "0.75rem",
      textTransform: "none",
      borderRadius: 0,
      backgroundColor: "hsl(var(--background))",
      color: "hsl(var(--foreground))",
      borderRight: "1px solid hsl(var(--border))",
      "&:hover": {
        backgroundColor: "hsl(var(--accent))",
      },
      "& .MuiButton-startIcon": {
        display: "none",
      },
    };

    const handleClick = () => {
      const id = randomId();
      setRows((oldRows) => [{ id, isNew: true }, ...oldRows]);
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: "DrawingName" },
      }));
    };

    return (
      <GridToolbarContainer>
        <Button variant="default" size="sm" onClick={handleClick}>
          <Plus className="h-4 w-4" />
          Add record
        </Button>
        <GridToolbarQuickFilter
          variant="outlined"
          size="small"
          sx={{
            minWidth: 180,
            paddingBottom: 0,
            fontSize: "0.75rem",
            "& .MuiOutlinedInput-root": {
              borderRadius: "calc(var(--radius) - 2px)",
              backgroundColor: "hsl(var(--background))",
              fontSize: "0.75rem",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "hsl(var(--border))",
            },
          }}
        />
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            position: "absolute",
            right: 8,
          }}
        >
          <div className="inline-flex rounded-md shadow-[0_0_0_1px_hsl(var(--border))] overflow-hidden">
            <GridToolbarFilterButton slotProps={{ button: { sx: gridButtonSx } }} />
            {selectionModel.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-none border-0 border-r"
                onClick={handleSelectRowDelete}
              >
                <Trash2 className="h-4 w-4" />
                선택 Row 삭제({selectionModel.length})
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-none border-0 border-r"
              onClick={downloadExcelModule}
            >
              <Download className="h-4 w-4" />
              모듈 정보 다운로드
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none border-0 border-r"
              onClick={downloadExcel}
            >
              <Download className="h-4 w-4" />
              다운로드
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none border-0 border-r"
              onClick={handleButtonClick}
            >
              <Upload className="h-4 w-4" />
              임포트
              <input
                type="file"
                accept=".csv, .xls, .xlsx"
                hidden
                ref={fileInputRef}
                onChange={onAdd}
              />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none border-0"
              onClick={handlerequest}
            >
              <Send className="h-4 w-4" />
              계획 확정
            </Button>
          </div>
        </div>
      </GridToolbarContainer>
    );
  }
  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  /////////validataion update////

  const processRowUpdate = (newRow) => {
    // Define the required fields and their error messages
    const requiredFields = [
      { field: "DrawingName", message: "도면명은 필수값입니다." },
      { field: "DPFloor", message: "층은 필수값입니다." },
      { field: "DrawingNo", message: "도면 번호는 필수값입니다." },
      { field: "DPsubPD2", message: "구분은 필수값입니다." },
      { field: "DPsubPD", message: "상세공종은 필수값입니다." },
      { field: "DrawingDiscPD", message: "공종은 필수값입니다." },
      { field: "DPAreaBPK", message: "Area는 필수값입니다." },
      { field: "DPRevNo", message: "도면 리비전 번호는 필수값입니다." },
    ];

    const date = new Date(newRow.WStart);
    const date2 = new Date(newRow.MDSitePlanDO);

    if (isNaN(date.getTime()) && isNaN(date2.getTime())) {
      throw new Error(
        `레코드 저장 중 에러 발생: 기준 날짜나 모듈 입고일 중 하나는 필수적으로 입력되어있어야합니다.`
      );
    }

    // Check each required field
    for (const { field, message } of requiredFields) {
      // Check if the value is not present
      if (!newRow[field]) {
        throw new Error(`레코드 저장 중 에러 발생: ${message}`);
      }

      // Check if the value is not a string or if the string value is empty
      if (typeof newRow[field] !== "string" || newRow[field].trim() === "") {
        throw new Error(`레코드 저장 중 에러 발생: ${message}`);
      }
    }

    // If all checks pass, update the row
    const updatedRow = {
      ...newRow,
      isNew: false,
      WStart: newRow.WStart === null ? newRow.MDSitePlanDO : newRow.WStart,
      ReceiptPlanDate: newRow.ReceiptPlanDate,
      CompletionPlanDate: newRow.CompletionPlanDate,
      // IssuePlanDate: newRow.IssuePlanDate,
      SubmissionPlanDate: newRow.SubmissionPlanDate,
      ApprovalPlanDate: newRow.ApprovalPlanDate,
    };

    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleProcessRowUpdateError = React.useCallback((error) => {
    Swal.fire({ title: "오류", text: error.message, icon: "error" });
  }, []);

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const [moduleTabeData, setModuleTabeData] = React.useState(moduleData);
  const [currentData, setCurrentData] = React.useState(null);

  function getCaculatedDate(value, row) {
    const key = projectCode.projectnumber + "승인도" + row.DPsubPD + row.DPsubPD2;

    const date =
      currentData && currentData.MDSitePlanDO !== undefined && currentData.id === row.id
        ? currentData.MDSitePlanDO
        : row.WStart;
    const field = row.field;
    const timeKey =
      field === "ReceiptPlanDate"
        ? "PreReviewSubmission"
        : field === "CompletionPlanDate"
          ? "PreReviewReceipt"
          : field === "SubmissionPlanDate"
            ? "CPMSSubmission"
            : field === "ApprovalPlanDate"
              ? "CPMSReceipt"
              : "DrawingIssue";

    const timegap =
      timeData && timeData.length > 0 && timeData.find((v) => v.uniqtext === key)
        ? timeData.find((v) => v.uniqtext === key)[timeKey]
        : 0;

    if (date) {
      // dayjs 객체를 사용하여 날짜를 계산하고, 이를 Date 객체로 변환
      return dayjs(date).add(timegap, "day").toDate();
    } else {
      // date 값이 없을 경우 현재 날짜를 반환
      return new Date();
    }
  }

  const handleModuleData = (data) => {
    if (data.filter((v) => v.selected).length > 0) {
      const selectedData = _.minBy(
        data.filter((v) => v.selected),
        "MDSitePlanDO"
      );
      const moduleNumbers = selectedData.MDModuleNumber;
      const moduleDate = dayjs(selectedData.MDSitePlanDO).toDate();

      // currentData.MDSitePlanDO = moduleDate;
      // currentData.MDModuleNoDPK = moduleNumbers;

      const updatedFields = {
        MDSitePlanDO: moduleDate,
        MDModuleNoDPK: moduleNumbers,
      };

      if (!editRow) {
        const updatedDataState = rows.map((item) => {
          if (item.id === currentData.id) {
            return { ...item, ...updatedFields };
          }
          return item;
        });

        setRows(updatedDataState);
      } else {
        const updatedDataState = rows.find((item) => {
          return item.id === currentData.id;
        });

        if (updatedDataState) {
          setEditRow((prev) => {
            return { ...prev, ...updatedFields };
          });
        }
      }
    }

    setOpen(false);
  };

  function AutocompleteCell(props) {
    const { id, value, field } = props;
    const apiRef = useGridApiContext();
    const [openPopover, setOpenPopover] = useState(false);

    const handleChange = (newValue) => {
      apiRef.current.setEditCellValue({ id, field, value: newValue });
      setOpenPopover(false);
    };

    return (
      <Popover open={openPopover} onOpenChange={setOpenPopover}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-9 w-[300px] items-center justify-start rounded-md border border-input bg-background px-3 py-1 text-left text-sm shadow-[0_1px_2px_0_rgb(0,0,0,0.05)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {value ? (
              <span className="truncate">{value}</span>
            ) : (
              <span className="text-muted-foreground">업체</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-2" align="start">
          <div className="max-h-60 space-y-1 overflow-y-auto">
            {vendorList.map((opt) => (
              <button
                key={opt}
                type="button"
                className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                onClick={() => handleChange(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  function getDate(value, row) {
    const date = row.MDSitePlanDO;
    const inputDate = value;

    if (date) {
      return dayjs(date).toDate();
    } else {
      return inputDate;
    }
  }

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<Save className="h-4 w-4" />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<X className="h-4 w-4" />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Pencil className="h-4 w-4" />}
            label="Edit"
            className="textPrimary"
            // onClick={handleEditClick(id)}
            onClick={handleOpenSheet(row)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<Trash2 className="h-4 w-4" />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
    GRID_CHECKBOX_SELECTION_COL_DEF,
    {
      field: "record_no",
      headerName: "레코드 번호",
      width: 100,
      editable: false,
    },
    {
      field: "DrawingDiscPD",
      headerName: "공종*",
      width: 80,
      editable: true,
      type: "singleSelect",
      valueOptions: DrawingDiscPDList,
    },
    {
      field: "DPsubPD",
      headerName: "상세공종*",
      width: 80,
      editable: true,
      type: "singleSelect",
      valueOptions: DPsubPDList,
    },
    {
      field: "DPsubPD2",
      headerName: "구분*",
      width: 80,
      editable: true,
      type: "singleSelect",
      valueOptions: ({ row }) => {
        if (!row) {
          return DPsubPD2List;
        }
        return row.DPsubPD === "배관"
          ? ["모듈", "비모듈"]
          : row.DPsubPD === "HVAC"
            ? ["GAL'V", "GAL'V 외"]
            : ["-"];
      },
    },
    {
      field: "DPAreaBPK",
      headerName: "Area*",
      width: 100,
      editable: true,
      type: "singleSelect",
      valueOptions: areaList,
    },
    { field: "DPFloor", headerName: "층*", width: 100, editable: true },
    {
      field: "DrawingNo",
      headerName: "도면(문서) No*",

      width: 180,
      editable: true,
    },
    {
      field: "DrawingName",
      headerName: "도면명*",
      width: 350,
      editable: true,
    },
    { field: "DPRevNo", headerName: "리비전*", width: 100, editable: true },
    {
      field: "VendorsDPK",
      headerName: "협력업체*",
      width: 180,
      editable: true,
      type: "singleSelect",
      renderEditCell: AutocompleteCell,
    },
    {
      field: "module",
      type: "actions",
      headerName: "Module 조회",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<Search className="h-4 w-4" />}
              label="Search"
              className="textPrimary"
              onClick={() => handleShowModal(id)}
              color="inherit"
            />,
          ];
        }

        return [];
      },
    },
    {
      field: "MDModuleNoDPK",
      headerName: "모듈넘버",
      width: 100,
      editable: false,
    },
    {
      field: "MDSitePlanDO",
      headerName: "모듈 입고일",
      width: 100,
      type: "date",
      editable: false,
    },
    {
      field: "WStart",
      headerName: "기준 날짜*",
      type: "date",
      width: 100,
      editable: true,
      renderCell: (params) => {
        const date = params.row.MDSitePlanDO;
        const inputDate = params.value;

        if (date) {
          return dayjs(date).format("YYYY-MM-DD");
        } else {
          return inputDate ? dayjs(inputDate).format("YYYY-MM-DD") : "";
        }
      },
    },

    {
      field: "ReceiptPlanDate",
      headerName: "접수 계획일",
      type: "date",
      width: 100,
      valueGetter: (value, row) => {
        const key = projectCode.projectnumber + "승인도" + row.DPsubPD + row.DPsubPD2;
        const date =
          currentData && currentData.MDSitePlanDO !== undefined && currentData.id === row.id
            ? currentData.MDSitePlanDO
            : row.WStart;
        const timeKey = "PreReviewSubmission";
        const timegap =
          timeData && timeData.length > 0 && timeData.find((v) => v.uniqtext === key)
            ? timeData.find((v) => v.uniqtext === key)[timeKey]
            : 0;

        const userInputDate = row.ReceiptPlanDate;

        if (userInputDate) {
          return dayjs(userInputDate).toDate();
        } else if (date) {
          return dayjs(date).add(timegap, "day").toDate();
        } else {
          return null;
        }
      },

      editable: true,
    },

    {
      field: "CompletionPlanDate",
      headerName: "완료 계획일",
      type: "date",
      width: 100,
      valueGetter: (value, row) => {
        const key = projectCode.projectnumber + "승인도" + row.DPsubPD + row.DPsubPD2;
        const date =
          currentData && currentData.MDSitePlanDO !== undefined && currentData.id === row.id
            ? currentData.MDSitePlanDO
            : row.WStart;
        const timeKey = "PreReviewReceipt";
        const timegap =
          timeData && timeData.length > 0 && timeData.find((v) => v.uniqtext === key)
            ? timeData.find((v) => v.uniqtext === key)[timeKey]
            : 0;

        const userInputDate = row.CompletionPlanDate;

        if (userInputDate) {
          return dayjs(userInputDate).toDate();
        } else if (date) {
          return dayjs(date).add(timegap, "day").toDate();
        } else {
          return null;
        }
      },

      editable: true,
    },
    // {
    //   field: "IssuePlanDate",
    //   headerName: "계획일",
    //   type: "date",
    //   width: 100,
    //   valueGetter: (value, row) => {
    //     const key =
    //       projectCode.projectnumber + "승인도" + row.DPsubPD + row.DPsubPD2;
    //     const date =
    //       currentData &&
    //       currentData.MDSitePlanDO !== undefined &&
    //       currentData.id === row.id
    //         ? currentData.MDSitePlanDO
    //         : row.WStart;
    //     const timeKey = "DrawingIssue";
    //     const timegap =
    //       timeData &&
    //       timeData.length > 0 &&
    //       timeData.find((v) => v.uniqtext === key)
    //         ? timeData.find((v) => v.uniqtext === key)[timeKey]
    //         : 0;

    //     const userInputDate = row.IssuePlanDate;

    //     if (userInputDate) {
    //       return dayjs(userInputDate).toDate();
    //     } else if (date) {
    //       return dayjs(date).add(timegap, "day").toDate();
    //     } else {
    //       return null;
    //     }
    //   },
    //   editable: false,
    // },
    {
      field: "SubmissionPlanDate",
      headerName: "상신 계획일",
      type: "date",
      width: 100,
      valueGetter: (value, row) => {
        const key = projectCode.projectnumber + "승인도" + row.DPsubPD + row.DPsubPD2;
        const date =
          currentData && currentData.MDSitePlanDO !== undefined && currentData.id === row.id
            ? currentData.MDSitePlanDO
            : row.WStart;
        const timeKey = "CPMSSubmission";
        const timegap =
          timeData && timeData.length > 0 && timeData.find((v) => v.uniqtext === key)
            ? timeData.find((v) => v.uniqtext === key)[timeKey]
            : 0;

        const userInputDate = row.SubmissionPlanDate;

        if (userInputDate) {
          return dayjs(userInputDate).toDate();
        } else if (date) {
          return dayjs(date).add(timegap, "day").toDate();
        } else {
          return null;
        }
      },

      editable: true,
    },
    {
      field: "ApprovalPlanDate",
      headerName: "승인 계획일",
      type: "date",
      width: 100,
      valueGetter: (value, row) => {
        const key = projectCode.projectnumber + "승인도" + row.DPsubPD + row.DPsubPD2;
        const date =
          currentData && currentData.MDSitePlanDO !== undefined && currentData.id === row.id
            ? currentData.MDSitePlanDO
            : row.WStart;
        const timeKey = "CPMSReceipt";
        const timegap =
          timeData && timeData.length > 0 && timeData.find((v) => v.uniqtext === key)
            ? timeData.find((v) => v.uniqtext === key)[timeKey]
            : 0;

        const userInputDate = row.ApprovalPlanDate;

        if (userInputDate) {
          return dayjs(userInputDate).toDate();
        } else if (date) {
          return dayjs(date).add(timegap, "day").toDate();
        } else {
          return null;
        }
      },

      editable: true,
    },
  ];

  const columnGroupingModel = [
    {
      groupId: "사전검토",
      children: [{ field: "ReceiptPlanDate" }, { field: "CompletionPlanDate" }],
    },
    // {
    //   groupId: "도면 ISSUE",
    //   children: [{ field: "IssuePlanDate" }],
    // },
    {
      groupId: "CPMS",
      children: [{ field: "SubmissionPlanDate" }, { field: "ApprovalPlanDate" }],
    },
  ];

  const excelColumns = [
    // { key: "record_no", displayName: "레코드 번호" },
    { key: "DrawingDiscPD", displayName: "공종", require: true },
    { key: "DPsubPD", displayName: "상세공종", require: true },
    { key: "DPsubPD2", displayName: "구분", require: true },
    { key: "DPAreaBPK", displayName: "Area", require: true },
    { key: "DPFloor", displayName: "층", require: true },
    { key: "DrawingNo", displayName: "도면(문서) No", require: true },
    { key: "DrawingName", displayName: "도면명", require: true },
    { key: "DPRevNo", displayName: "리비전", require: true },
    { key: "MDModuleNoDPK", displayName: "모듈넘버" },
    { key: "WStart", displayName: "기준 날짜", require: true },
    { key: "VendorsDPK", displayName: "협력업체", require: true },
  ];

  const downloadExcel = async () => {
    // 새 워크북 생성
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");

    // 열 정의 (스타일 없음)
    const columns = excelColumns.map((col) => ({
      header: col.require ? `${col.displayName}*` : col.displayName,
      key: col.key,
      width: col.displayName.length + 2, // 초기 열 너비 설정
    }));
    worksheet.columns = columns;

    // 데이터 행 추가
    rows.forEach((row) => {
      const newRow = {};
      Object.keys(row).forEach((key) => {
        if (["WStart", "MDSitePlanDO"].includes(key) && row[key]) {
          newRow[key] = dayjs(row[key]).format("YYYY-MM-DD"); // dayjs를 사용하여 날짜 포맷 변경
        } else {
          newRow[key] = row[key];
        }
      });
      worksheet.addRow(newRow);
    });

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell, colNumber) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" }, // 회색 배경
      };
      cell.font = {
        bold: true,
        color: excelColumns[colNumber - 1].require ? { argb: "FFFF0000" } : { argb: "FF000000" },
      };
    });

    // 열 너비

    // 열 너비 조정
    worksheet.columns.forEach((column) => {
      let maxWidth = 10; // 최소 너비
      column.eachCell({ includeEmpty: true }, (cell) => {
        let cellLength = cell.value ? cell.value.toString().length : 0;
        if (cellLength > maxWidth) {
          maxWidth = cellLength;
        }
      });
      column.width = maxWidth + 2;
    });

    // A열에 드롭다운 리스트 추가
    let validationType = {
      type: "list",
      formulae: ['"설비,건축,전기,제어"'],
      showDropDown: true,
    };

    let validationDetailType = {
      type: "list",
      formulae: ['"배관,건축,랙,전기,제어,설비제어,전기제어,HVAC"'],
      showDropDown: true,
    };

    const Gubun = ["모듈", "비모듈", "-", "GAL'V", "GAL'V 외"];
    const GubunString = Gubun.map((item) => item).join(",");

    let validationGubunType = {
      type: "list",
      formulae: [`"${GubunString}"`],
      showDropDown: true,
    };

    const AreaString = areaList.map((item) => item).join(",");

    let validationAreaType = {
      type: "list",
      formulae: [`"${AreaString}"`],
      showDropDown: true,
    };

    // A열의 1행부터 5000행까지 드롭다운 적용
    for (let i = 2; i <= 5000; i++) {
      worksheet.getCell(`A${i}`).dataValidation = validationType;
      worksheet.getCell(`B${i}`).dataValidation = validationDetailType;
      worksheet.getCell(`C${i}`).dataValidation = validationGubunType;
      worksheet.getCell(`D${i}`).dataValidation = validationAreaType;
    }
    // 파일 생성 및 저장
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `승인도_dp_계획작성_${dayjs().format("YYYYMMDDHHmmss")}.xlsx`);
  };

  const onAdd = (event) => {
    if (!event.target.files) {
      return;
    }

    const file = event.target.files[0];

    const reader = new FileReader();
    reader.onloadend = (ev) => {
      if (!ev?.target?.result) {
        return;
      }

      const wb = read(ev.target.result, {
        type: "binary",
        cellText: false,
        cellDates: true,
      });
      const sheets = wb.SheetNames;

      const header = utils
        .sheet_to_json(wb.Sheets[sheets[0]], { header: 1 })[0]
        .map((v) => v.replace(/\*/g, ""));
      const excelColumnsValues = excelColumns.map((v) => v.displayName);

      if (sheets.length) {
        if (
          header.length === excelColumnsValues.length &&
          header.every(function (value, index) {
            return value === excelColumnsValues[index];
          })
        ) {
          const allData = utils.sheet_to_json(wb.Sheets[sheets[0]], {
            header: 1,
            blankrows: false,
          });

          const excelHeader = allData[0].map((cell) => (cell ? cell.replace(/\*/g, "") : cell));
          const dataRows = allData.slice(1);

          const excelrows = dataRows
            .map((row) => {
              return excelHeader.reduce((newRow, header, index) => {
                newRow[header] = row[index];
                return newRow;
              }, {});
            })
            .map((row) =>
              excelColumns.reduce((obj, col) => {
                if (
                  [
                    "WStart",
                    "ReceiptPlanDate",
                    "CompletionPlanDate",
                    // "IssuePlanDate",
                    "SubmissionPlanDate",
                    "ApprovalPlanDate",
                  ].includes(col.key) &&
                  row[col.displayName]
                ) {
                  obj[col.key] = new Date(row[col.displayName]);
                } else {
                  obj[col.key] = row[col.displayName];
                }
                return obj;
              }, {})
            );

          const errorMessages = [];

          const allowedDrawingDiscPD = DrawingDiscPDList;
          const allowedDPsubPD = DPsubPDList;
          const allowedDPsubPD2 = DPsubPD2List;

          // Check each row for valid values
          for (let i = 0; i < excelrows.length; i++) {
            const row = excelrows[i];
            let errorsForRow = [];

            let invalidColumn = "";

            let requiredColumns;
            let convertedColumns;
            if (
              row.MDModuleNoDPK !== "" &&
              row.MDModuleNoDPK !== null &&
              row.MDModuleNoDPK !== undefined
            ) {
              requiredColumns = [
                "DrawingDiscPD",
                "DPsubPD",
                "DPsubPD2",
                "DPAreaBPK",
                "DPFloor",
                "DrawingNo",
                "DrawingName",
                "DPRevNo",
                "MDModuleNoDPK",
                "VendorsDPK",
              ];
              convertedColumns = [
                "공종",
                "상세공종",
                "구분",
                "Area",
                "층",
                "도면(문서) No",
                "도면명",
                "리비전",
                "모듈넘버",
                "협력업체",
              ];
            } else {
              requiredColumns = [
                "DrawingDiscPD",
                "DPsubPD",
                "DPsubPD2",
                "DPAreaBPK",
                "DPFloor",
                "DrawingNo",
                "DrawingName",
                "DPRevNo",
                "WStart",
                "VendorsDPK",
              ];
              convertedColumns = [
                "공종",
                "상세공종",
                "구분",
                "Area",
                "층",
                "도면(문서) No",
                "도면명",
                "리비전",
                "기준 날짜",
                "협력업체",
              ];
            }

            requiredColumns.forEach((col, index) => {
              if (!row[col] && row[col] !== 0) {
                errorsForRow.push(`'${convertedColumns[index]}' 필드가 누락되었습니다.`);
              }
            });

            if (!allowedDrawingDiscPD.includes(row.DrawingDiscPD)) {
              invalidColumn = "공종";
            } else if (!allowedDPsubPD.includes(row.DPsubPD)) {
              invalidColumn = "상세공종";
            } else if (!allowedDPsubPD2.includes(row.DPsubPD2)) {
              invalidColumn = "구분";
            } else if (!vendorList.includes(row.VendorsDPK)) {
              invalidColumn = "업체명";
            } else if (!areaList.includes(row.DPAreaBPK)) {
              invalidColumn = "Area";
            }

            if (invalidColumn) {
              errorsForRow.push(`${invalidColumn} 값을 확인하세요.`);
            }

            if (errorsForRow.length > 0) {
              errorMessages.push({ row: i, errors: errorsForRow });
            }
          }

          if (errorMessages.length > 0) {
            const transformedRows = excelrows.map((row) => {
              const newRow = {};
              excelColumns.forEach((column) => {
                newRow[column.displayName] = row[column.key] || ""; // column.key가 없는 경우 빈 문자열을 할당
              });
              return newRow;
            });

            transformedRows.forEach((row, index) => {
              const errorForRow = errorMessages.find((e) => e.row === index);
              if (errorForRow) {
                row["error"] = errorForRow.errors.join("; ");
              }
            });
            const updatedWorkbook = utils.book_new();
            const updatedSheet = utils.json_to_sheet(transformedRows);

            utils.book_append_sheet(updatedWorkbook, updatedSheet, "Sheet1");
            writeFile(updatedWorkbook, `임포트 오류_${dayjs().format("YYYYMMDDHHmmss")}.xlsx`);

            Swal.fire({
              icon: "error",
              title: "오류가 발견되었습니다. 다운로드된 엑셀 파일을 확인하세요.",
              confirmButtonText: "확인",
            });
          } else {
            excelrows.forEach((row) => {
              // row가 유효하고 MDModuleNoDPK 속성이 존재하며, 이 속성이 null이나 undefined가 아닌지 확인
              if (row && row.hasOwnProperty("MDModuleNoDPK") && row["MDModuleNoDPK"]) {
                const moduleNumbers = row["MDModuleNoDPK"].split(",");

                // 일치하는 MDModuleNumber 항목 필터링
                const matchedModules = moduleData.filter((md) =>
                  moduleNumbers.includes(md.MDModuleNumber)
                );

                // 가장 작은 MDSitePlanDO를 가진 항목 찾기
                const earliestModule = _.minBy(matchedModules, "MDSitePlanDO");

                // 필요한 경우 row 업데이트
                if (earliestModule) {
                  // Date 객체로 변환 (시간대 조정 포함)
                  const date = new Date(earliestModule.MDSitePlanDO);
                  date.setHours(date.getHours() + 9); // UTC+9 (한국 시간)로 조정

                  row["WStart"] = date;
                  row["MDModuleNoDPK"] = earliestModule.MDModuleNumber;
                  row["MDSitePlanDO"] = date;
                }
              }

              // 다른 날짜 필드들도 Date 객체로 변환
              [
                "WStart",
                "ReceiptPlanDate",
                "CompletionPlanDate",
                // "IssuePlanDate",
                "SubmissionPlanDate",
                "ApprovalPlanDate",
              ].forEach((dateField) => {
                if (row[dateField]) {
                  const date = new Date(row[dateField]);
                  date.setHours(date.getHours() + 9); // UTC+9 (한국 시간)로 조정
                  row[dateField] = date;
                }
              });
            });

            const add_row = _.differenceBy(
              excelrows.map((v) => ({
                ...v,
                uniq: v.DrawingNo + v.DrawingName + v.DPRevNo,
              })),
              rows.map((v) => ({
                ...v,
                uniq: v.DrawingNo + v.DrawingName + v.DPRevNo,
              })),
              "uniq"
            )
              .map((v) => {
                delete v.record_no;
                return v;
              })
              .map((v) => ({ ...v, id: randomId() }));
            const exist = _.differenceBy(
              excelrows.map((v) => ({
                ...v,
                uniq: v.DrawingNo + v.DrawingName + v.DPRevNo,
              })),
              add_row.map((v) => ({
                ...v,
                uniq: v.DrawingNo + v.DrawingName + v.DPRevNo,
              })),
              "uniq"
            );

            const updatedData = exist
              .map((v) => {
                const recordNo =
                  rows.find((item) => item.DrawingNo + item.DrawingName + item.DPRevNo === v.uniq)
                    ?.record_no || "";
                return { ...v, record_no: recordNo };
              })
              .map((v) => ({
                ...v,
                id: rows.find((vf) => vf.record_no === v.record_no).id,
              }));
            // 새로운 행 추가
            const newDataState = [...updatedData, ...add_row];
            setRows(newDataState);

            // setState2(!state)
            Swal.fire({
              icon: "success",
              title:
                "엑셀을 테이블로 로드했습니다. 데이터에 문제가 없으면 계획 확정 버튼을 클릭하세요",
              confirmButtonText: "확인",
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "임포트한 엑셀 헤더를 확인하세요. 다운로드에 있는 헤더를 사용해야합니다.",
            confirmButtonText: "확인",
          });
        }
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handlerequest = () => {
    const keysToCompare = [
      "VendorsDPK",
      "DPRevNo",
      "DPAreaBPK",
      "DPFloor",
      "DPsubPD",
      "DPsubPD2",
      "DrawingDiscPD",
      "DrawingName",
      "DrawingNo",
      "MDModuleNoDPK",
      "WStart",
      "DPRevNo",
      "ReceiptPlanDate",
      "CompletionPlanDate",
      // "IssuePlanDate",
      "SubmissionPlanDate",
      "ApprovalPlanDate",
    ];
    const values = [
      "공종",
      "상세공종",
      "구분",
      "Area",
      "층",
      "도면(문서) No",
      "도면명",
      "리비전",
      "기준 날짜",
      "협력업체",
      "접수 계획일",
      "완료 계획일",
      // "계획일",
      "상신 계획일",
      "승인 계획일",
    ];
    const keys = [
      "DrawingDiscPD",
      "DPsubPD",
      "DPsubPD2",
      "DPAreaBPK",
      "DPFloor",
      "DrawingNo",
      "DrawingName",
      "DPRevNo",
      "WStart",
      "VendorsDPK",
      "ReceiptPlanDate",
      "CompletionPlanDate",
      // "IssuePlanDate",
      "SubmissionPlanDate",
      "ApprovalPlanDate",
    ];

    const processedRows = rows.map((row) => {
      const newRow = { ...row };
      const key = projectCode.projectnumber + "승인도" + row.DPsubPD + row.DPsubPD2;
      const defaultDate = row.MDSitePlanDO || row.WStart;

      const calculateDate = (fieldName, timeKey) => {
        if (newRow[fieldName] === null || newRow[fieldName] === undefined) {
          const timegap =
            timeData && timeData.length > 0 && timeData.find((v) => v.uniqtext === key)
              ? timeData.find((v) => v.uniqtext === key)[timeKey]
              : 0;
          return defaultDate ? dayjs(defaultDate).add(timegap, "day").toDate() : null;
        }
        return newRow[fieldName];
      };

      newRow.ReceiptPlanDate = calculateDate("ReceiptPlanDate", "PreReviewSubmission");
      newRow.CompletionPlanDate = calculateDate("CompletionPlanDate", "PreReviewReceipt");
      // newRow.IssuePlanDate = calculateDate("IssuePlanDate", "DrawingIssue");
      newRow.SubmissionPlanDate = calculateDate("SubmissionPlanDate", "CPMSSubmission");
      newRow.ApprovalPlanDate = calculateDate("ApprovalPlanDate", "CPMSReceipt");

      return newRow;
    });

    for (let row of processedRows) {
      for (let i = 0; i < keys.length; i++) {
        if (row[keys[i]] === "" || row[keys[i]] === null) {
          Swal.fire({
            title: "Error",
            text: `Missing or empty value for "${values[i]}" in one or more records.`,
            icon: "error",
            confirmButtonText: "Ok",
          });
          return; // Exit the function early
        }
      }
    }

    const updateData = processedRows
      .filter((v) => v.record_no !== undefined && v.record_no !== "")
      .map((v) => ({
        ...v,
        WStart: dayjs(v.WStart).format("MM-DD-YYYY"),
        ReceiptPlanDate: dayjs(v.ReceiptPlanDate).format("MM-DD-YYYY"),
        CompletionPlanDate: dayjs(v.CompletionPlanDate).format("MM-DD-YYYY"),
        // IssuePlanDate: dayjs(v.IssuePlanDate).format("MM-DD-YYYY"),
        SubmissionPlanDate: dayjs(v.SubmissionPlanDate).format("MM-DD-YYYY"),
        ApprovalPlanDate: dayjs(v.ApprovalPlanDate).format("MM-DD-YYYY"),
      }))
      .filter((dataStateElement) => {
        const tableDataElement = tabeldata
          .map((v) => ({ ...v, WStart: dayjs(v.WStart).format("MM-DD-YYYY") }))
          .find((e) => e.record_no === dataStateElement.record_no);
        if (!tableDataElement) return false;

        return keysToCompare.some((key) => dataStateElement[key] !== tableDataElement[key]);
      })
      .map((v) => ({
        ...v,
        DPLeadTimeDP: projectCode.projectnumber + "승인도" + v.DPsubPD + v.DPsubPD2,
        uniqtext: projectCode.projectnumber + "승인도" + v.DPsubPD + v.DPsubPD2,
      }))
      .map((v) => {
        delete v.ReceiptActualDate;
        delete v.SubmissionActualDate;
        delete v.ApprovalActualDate;
        delete v.CompletionActualDate;
        delete v.IssueActualDate;

        return v;
      });

    const createData = processedRows
      .filter((v) => v.record_no === undefined || v.record_no === "")
      .map((v) => ({
        ...v,
        WStart: dayjs(v.WStart).format("MM-DD-YYYY"),
        ReceiptPlanDate: dayjs(v.ReceiptPlanDate).format("MM-DD-YYYY"),
        CompletionPlanDate: dayjs(v.CompletionPlanDate).format("MM-DD-YYYY"),
        // IssuePlanDate: dayjs(v.IssuePlanDate).format("MM-DD-YYYY"),
        SubmissionPlanDate: dayjs(v.SubmissionPlanDate).format("MM-DD-YYYY"),
        ApprovalPlanDate: dayjs(v.ApprovalPlanDate).format("MM-DD-YYYY"),
        DPLeadTimeDP: projectCode.projectnumber + "승인도" + v.DPsubPD + v.DPsubPD2,
      }))
      .map((v) => {
        delete v.ReceiptActualDate;
        delete v.SubmissionActualDate;
        delete v.ApprovalActualDate;
        delete v.CompletionActualDate;
        delete v.IssueActualDate;

        return v;
      });

    // Rest of the function remains the same...
    Swal.fire({
      title: "정말 승인도 DP 계획을 유니파이어로 보내겠습니까?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "네",
      cancelButtonText: "아니오",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .post("/submitDP", {
            type: "승인도",
            project: projectCode.projectnumber,
            updateData: updateData,
            createData: createData,
          })
          .then((response) => {
            if (response.data.success) {
              Swal.fire({
                title: "성공적으로 제출되었습니다",
                icon: "success",
                confirmButtonText: "확인",
              }).then(() => {
                navigate("/adrawing");
              });
            } else {
              throw new Error(response.data.message);
            }
          })
          .catch((err) => {
            Swal.fire({
              title: "에러 발생",
              icon: "error",
              text: err.message || "An unknown error occurred",
              confirmButtonText: "확인",
            });
          });
      }
    });
  };

  const [open, setOpen] = React.useState(false);

  const handleShowModal = (id) => {
    setOpen(true);
    setCurrentData(rows.find((vf) => vf.id === id));
    if (moduleTabeData.length > 0) {
      setModuleTabeData(moduleTabeData.map((v) => ({ ...v, selected: false })));
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const dialogNodeRef = useRef(null);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
        {open && (
          <Draggable
            nodeRef={dialogNodeRef}
            handle="#draggable-dialog-title"
            cancel='[class*="dialog-content-scrollable"]'
            positionOffset={{ x: "-50%", y: "-50%" }}
          >
            <DialogContent
              ref={dialogNodeRef}
              aria-describedby={undefined}
              className="z-[60] max-w-[800px] translate-x-0 translate-y-0 p-0"
            >
              <DialogTitle
                id="draggable-dialog-title"
                style={{ cursor: "move" }}
                className="px-6 py-4 text-lg font-semibold"
              >
                {currentData && currentData["DrawingNo"] + "를 위한 모듈 조회"}
              </DialogTitle>
              <div className="dialog-content-scrollable px-6 pb-4">
                <ModuleTable moduletabledata={moduleData} onModuleDataHandle={handleModuleData} />
              </div>
              <div className="flex justify-end px-6 pb-4">
                <Button variant="ghost" autoFocus onClick={handleClose}>
                  취소
                </Button>
              </div>
            </DialogContent>
          </Draggable>
        )}
      </Dialog>
      <div
        className="flex-1 min-h-0 w-full rounded-lg border border-border overflow-hidden shadow-sm [&_.actions]:text-muted-foreground [&_.textPrimary]:text-foreground"
      >
        <DataGridPremium
          ignoreValueFormatterDuringExport
          cellSelection
          rows={rows}
          initialState={{ pinnedColumns: { left: ["actions"] } }}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={handleSelectionChange}
          rowSelectionModel={selectionModel}
          density="compact"
          columns={columns}
          isCellEditable={false}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          localeText={{ toolbarColumns: "컬럼", toolbarFilters: "필터" }}
          slots={{
            toolbar: EditToolbar,
          }}
          slotProps={{
            toolbar: { setRows, setRowModesModel },
          }}
          pagination
          experimentalFeatures={{ columnGrouping: true }}
          columnGroupingModel={columnGroupingModel}
          sx={{
            "& .MuiDataGrid-columnHeader .MuiSvgIcon-root, & .MuiDataGrid-cell .MuiSvgIcon-root, & .MuiCheckbox-root .MuiSvgIcon-root":
              {
                fontSize: "1rem",
              },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-cell": {
              fontSize: "0.75rem",
            },
            "& .MuiDataGrid-toolbarContainer": {
              backgroundColor: "hsl(var(--background))",
            },
          }}
        />
      </div>
      {openSheet && (
        <AdrawingtableDrawer
          title={"승인도 - 계획 작성"}
          open={openSheet}
          rows={rows}
          rowIndex={selectedRowIndex}
          editRow={editRow}
          onClose={() => {
            setOpenSheet(false);
            setEditRow(null);
          }}
          onChangeRow={setEditRow}
          onMovePrev={handlePrevRow}
          onMoveNext={handleNextRow}
          onSave={handleSheetSave}
          handleShowModal={handleShowModal}
          moduleDialogOpen={open}
          areaList={areaList}
          vendorList={vendorList}
        />
      )}
    </div>
  );
};
